import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["joueur1Select", "joueur2Select", "poidsCalcule", "submitButton"]

  connect() {
    // Bind the handler once and store reference for cleanup
    this.boundTomSelectReadyHandler = this.handleTomSelectReady.bind(this)
    this.element.addEventListener('tomselect:ready', this.boundTomSelectReadyHandler)

    // Listen for reset event from Turbo Stream
    this.boundResetHandler = this.handleReset.bind(this)
    this.element.addEventListener('team-form:reset', this.boundResetHandler)

    // Get tournament type from data attribute if available
    this.tournamentType = this.element.dataset.tournamentType || ""
    this.minPoidsPaire = this.getMinPoidsPaire()
  }

  getMinPoidsPaire() {
    // Minimum poids de paire selon le type de tournoi
    const minValues = {
      'P100': 1000,
      'P250': 1500,
      'P500': 2000,
      'P1000': 3000,
      'P1500': 4000,
      'P2000': 5000
    }
    return minValues[this.tournamentType] || 0
  }

  handleReset(event) {
    this.reset()
  }

  handleTomSelectReady(event) {
    this.checkAndInitialize()
  }

  checkAndInitialize() {
    // Only initialize once both targets are connected AND have TomSelect instances
    if (this.hasJoueur1SelectTarget &&
        this.hasJoueur2SelectTarget &&
        this.joueur1SelectTarget.tomselect &&
        this.joueur2SelectTarget.tomselect &&
        !this.initialized) {

      this.initialized = true

      this.tom1 = this.joueur1SelectTarget.tomselect
      this.tom2 = this.joueur2SelectTarget.tomselect

      this.initializeOptions()
    }
  }

  disconnect() {
    // Remove event listeners
    if (this.boundTomSelectReadyHandler) {
      this.element.removeEventListener('tomselect:ready', this.boundTomSelectReadyHandler)
      this.boundTomSelectReadyHandler = null
    }

    if (this.boundResetHandler) {
      this.element.removeEventListener('team-form:reset', this.boundResetHandler)
      this.boundResetHandler = null
    }

    // Unregister TomSelect event handlers
    if (this.tom1) {
      this.tom1.off('change')
    }
    if (this.tom2) {
      this.tom2.off('change')
    }

    // Clear references
    this.initialized = false
    this.tom1 = null
    this.tom2 = null
    this.originalOptions1 = null
    this.originalOptions2 = null
  }

  initializeOptions() {
    if (!this.tom1 || !this.tom2) return

    // Store original options from TomSelect instances
    this.originalOptions1 = Object.values(this.tom1.options)
    this.originalOptions2 = Object.values(this.tom2.options)

    // Use TomSelect's native onChange callback
    this.tom1.on('change', () => {
      this.filterJoueur2Options()
      this.calculatePoidsPaire()
    })

    this.tom2.on('change', () => {
      this.filterJoueur1Options()
      this.calculatePoidsPaire()
    })
  }

  calculatePoidsPaire() {
    if (!this.tom1 || !this.tom2) return

    const joueur1Id = this.tom1.getValue()
    const joueur2Id = this.tom2.getValue()

    if (joueur1Id && joueur2Id) {
      // Get player data from options
      const joueur1Option = this.tom1.options[joueur1Id]
      const joueur2Option = this.tom2.options[joueur2Id]

      if (joueur1Option && joueur2Option) {
        // Extract points from option text (format: "Nom (XXX pts)")
        const points1 = this.extractPoints(joueur1Option.text)
        const points2 = this.extractPoints(joueur2Option.text)

        const totalPoids = points1 + points2

        // Update display
        if (this.hasPoidsCalculeTarget) {
          this.poidsCalculeTarget.textContent = totalPoids

          // Add visual feedback for minimum requirement
          if (this.minPoidsPaire > 0) {
            if (totalPoids < this.minPoidsPaire) {
              this.poidsCalculeTarget.classList.add('text-red-600', 'font-bold')
              this.poidsCalculeTarget.classList.remove('text-green-600')
              this.poidsCalculeTarget.textContent = `${totalPoids} (min: ${this.minPoidsPaire})`
            } else {
              this.poidsCalculeTarget.classList.add('text-green-600', 'font-bold')
              this.poidsCalculeTarget.classList.remove('text-red-600')
              this.poidsCalculeTarget.textContent = `${totalPoids} ✓`
            }
          }
        }

        // Enable/disable submit button based on validation
        this.validateSubmit(totalPoids)
      }
    } else {
      // Reset display when selection incomplete
      if (this.hasPoidsCalculeTarget) {
        this.poidsCalculeTarget.textContent = '-'
        this.poidsCalculeTarget.classList.remove('text-red-600', 'text-green-600', 'font-bold')
      }
      this.validateSubmit(0)
    }
  }

  extractPoints(text) {
    // Extract points from format "Nom (XXX pts)" or "Nom - XXX pts"
    const match = text.match(/(\d+)\s*(?:pts?|points?)/i)
    return match ? parseInt(match[1]) : 0
  }

  validateSubmit(totalPoids) {
    if (this.hasSubmitButtonTarget) {
      if (this.minPoidsPaire > 0 && totalPoids < this.minPoidsPaire) {
        // Disable submit and show warning
        this.submitButtonTarget.disabled = true
        this.submitButtonTarget.classList.add('opacity-50', 'cursor-not-allowed')
        this.submitButtonTarget.title = `Poids de paire insuffisant (min: ${this.minPoidsPaire})`
      } else if (totalPoids > 0) {
        // Enable submit
        this.submitButtonTarget.disabled = false
        this.submitButtonTarget.classList.remove('opacity-50', 'cursor-not-allowed')
        this.submitButtonTarget.title = ''
      }
    }
  }

  reset() {
    // Simple clear() works perfectly - it triggers change events that handle filtering
    if (this.tom1) {
      this.tom1.clear()
      this.tom1.clearOptions()
    }

    if (this.tom2) {
      this.tom2.clear()
      this.tom2.clearOptions()
    }
  }


  filterJoueur1Options() {
    if (!this.tom1 || !this.tom2 || !this.originalOptions1) return

    const selectedJoueur2Id = this.tom2.getValue()

    // Clear and restore all options
    this.tom1.clearOptions()
    this.originalOptions1.forEach(opt => this.tom1.addOption(opt))

    // Remove the selected joueur2 from joueur1
    if (selectedJoueur2Id) {
      this.tom1.removeOption(selectedJoueur2Id)
    }

    this.tom1.refreshOptions(false)
  }

  filterJoueur2Options() {
    if (!this.tom1 || !this.tom2 || !this.originalOptions2) return

    const selectedJoueur1Id = this.tom1.getValue()

    // Clear and restore all options
    this.tom2.clearOptions()
    this.originalOptions2.forEach(opt => this.tom2.addOption(opt))

    // Remove the selected joueur1 from joueur2
    if (selectedJoueur1Id) {
      this.tom2.removeOption(selectedJoueur1Id)
    }

    this.tom2.refreshOptions(false)
  }
}
